import React from 'react';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NotesIcon from '@mui/icons-material/Notes';
import { ReactNode } from 'react';
import { useMediaQuery, useScrollTrigger } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  appBarStyles,
  Drawer,
  DrawerHeader,
  drawerHeaderCloseStyles,
  drawerHeaderIconStyles,
  drawerHeaderOpenStyles,
  drawerHeaderStyles,
  elevationScrollStyles,
  firstPageIconStyles,
  HideButton,
  MainBox,
  mobileMenuWrapperStyles,
  muiDrawerBackdropStyles,
  muiDrawerStyles,
  OpenIconButton,
  signOutIconStyles,
  toolbarStyles
} from './styles';
import { useTranslation } from 'react-i18next';
import SignOutIcon from '../icons/SignOut';
import { useMenu } from '../providers/MenuProvider';

interface ElevationScrollProps {
  window?: () => Window;
  children?: React.ReactElement<{ sx?: any }>;
}

function ElevationScroll(props: ElevationScrollProps) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return children
    ? React.cloneElement(children, {
        sx: { ...(trigger ? elevationScrollStyles : {}) },
      })
    : null;
}

export default function Page({ Logo, Header, Sidebar, accountType, children, keycloak }: {
  Logo: (props: any) => JSX.Element;
  Header: (props: any) => JSX.Element;
  Sidebar: (props: any) => ReactNode;
  keycloak: { logout: () => void };
  accountType: string;
  children: ReactNode;
}) {
  const { isOpen, setIsOpen } = useMenu();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMobileXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const handleDrawerOpen = () => {
    setIsOpen((prevState) => !prevState);
  };

const ResponsedDrawer = ({ children, isOpen }: { children: ReactNode; isOpen: boolean }) => {
  if (isMobile) {
    return (
      <MuiDrawer
        variant="temporary"
        open={isOpen}
        onClose={handleDrawerOpen}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: muiDrawerBackdropStyles
          },
        }}
        sx={muiDrawerStyles}
      >
        {children}
      </MuiDrawer>
    );
  }
  
  return (
    <Drawer
      variant="permanent"
      open={isOpen}
    >
      {children}
    </Drawer>
  );
};

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <ElevationScroll>
        <AppBar open={isOpen} sx={appBarStyles}>
          <Toolbar sx={toolbarStyles}>
            <Box sx={mobileMenuWrapperStyles}>
              <IconButton disableRipple onClick={handleDrawerOpen} size="large" aria-label="open drawer">
                <NotesIcon />
              </IconButton>
              {isMobile && (<Logo isOpen={true} />)}
            </Box>
            <Header />
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <ResponsedDrawer isOpen={isOpen}>
        <DrawerHeader sx={{ ...(isOpen ? drawerHeaderOpenStyles : drawerHeaderCloseStyles ) }}>
          {isMobile && <IconButton sx={drawerHeaderIconStyles} onClick={handleDrawerOpen}><CloseIcon /></IconButton>}
          <Logo isOpen={isOpen} />
        </DrawerHeader>
        <Sidebar isOpen={isOpen} accountType={accountType} />
        {!isMobile && (!isOpen ? <OpenIconButton
                      disableRipple
                      color="inherit"
                      aria-label="open drawer"
                      onClick={handleDrawerOpen}
                      edge="start"
                    >
                      <LastPageIcon />
                    </OpenIconButton>
                  :
                    <HideButton disableRipple onClick={handleDrawerOpen} size="large" startIcon={<FirstPageIcon sx={firstPageIconStyles} />}>{t('hideMenu')}</HideButton>)}

         {isMobileXs && (
            <HideButton disableRipple onClick={keycloak.logout} size="small" startIcon={<SignOutIcon sx={signOutIconStyles} />}>{t("signOut")}</HideButton>
         )}           
      </ResponsedDrawer>
      <MainBox component="main">
        <DrawerHeader sx={drawerHeaderStyles}>
          <Header openMenuHandler={handleDrawerOpen} />
        </DrawerHeader>
        {children}
      </MainBox>
    </Box>
  );
}